import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 0,
  class: "font-medium"
}
const _hoisted_3 = { class: "bg-white border border-gray-200 rounded-b-lg px-6 shadow" }
const _hoisted_4 = { class: "flex justify-between my-6 items-end" }
const _hoisted_5 = { class: "flex-col" }
const _hoisted_6 = { class: "flex" }
const _hoisted_7 = {
  key: 0,
  class: "text-success-800"
}
const _hoisted_8 = {
  key: 0,
  class: "items-center"
}
const _hoisted_9 = { class: "flex" }
const _hoisted_10 = { class: "text-gray-900 text-base mr-2" }
const _hoisted_11 = { class: "text-gray-400" }
const _hoisted_12 = {
  key: 1,
  class: "text-danger-700"
}
const _hoisted_13 = { class: "items-center" }
const _hoisted_14 = { class: "flex" }
const _hoisted_15 = { class: "text-base mr-2" }
const _hoisted_16 = { class: "text-gray-400" }
const _hoisted_17 = {
  key: 2,
  class: "text-primary-500 items-center"
}
const _hoisted_18 = {
  key: 0,
  class: "items-center"
}
const _hoisted_19 = { class: "flex" }
const _hoisted_20 = { class: "text-gray-900 text-base mr-2" }
const _hoisted_21 = { class: "text-gray-400" }

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import vueFilePond from 'vue-filepond';
import {
  FilePondErrorDescription,
  FilePondFile,
  FilePondInitialFile,
  FilePondServerConfigProps,
  FileStatus
} from 'filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import BaseIcon from '@/lib/components/Icon/BaseIcon.vue';
import MedKitButton from '@/lib/components/Button/MedKitButton.vue';
import LoadingBar from '@/lib/components/Loading/LoadingBar.vue';
import InputTextLabel from '@/lib/components/Helper/InputTextLabel.vue';
import ActionModal from '@/lib/components/Modals/ActionModal.vue';
import { FileAnswer, FileUploaded } from '@/models/file.model';
import { apiClient } from '@/services/api';
import cookieService from '@/services/cookie.service';
import tokenService from '@/services/token.service';
import { useSessionStore } from '@/stores/session.store';
import { useNotificationStore } from '@/stores/notification.store';
import { formatBytes } from '../../helpers/file.helper';
import { FILE_UPLOAD_CONSTANTS } from '@/constants/file-upload';

interface Props {
  /** Maximum file size limit (e.g., '10MB') */
  fileSizeLimit?: string;
  /** Allowed file extensions (e.g., 'application/mxf,.mxf') */
  extension?: string;
  /** Label for the upload field */
  label?: string;
  /** Whether the component is in readonly mode */
  readonly?: boolean;
  /** Whether the field is optional */
  isOptional?: boolean;
  /** Base server URL for file uploads */
  serverUrl?: string;
  /** Array of uploaded files */
  modelValue?: FileAnswer[];
  /** Error message to display */
  error?: string | null;
  /** Whether to hide uploaded files list */
  hideUploadedFiles?: boolean;
  /** Whether to hide upload progress */
  hideProgress?: boolean;
  /** Patient ID for scan-specific uploads */
  patientId?: string;
  /** Whether to append scan-specific parameters */
  appendScanParams?: boolean;
  /** Optional height for the dropzone area (e.g., '32.5rem', '520px') */
  dropzoneHeight?: string;
  /** Optional callback to fetch file info by id (moves API out of component) */
  fetchFileInfo?: (fileId: string, filename?: string) => Promise<FileAnswer | null>;
}

// ===== COMPONENT SETUP =====

export default /*@__PURE__*/_defineComponent({
  __name: 'ScanUploader',
  props: {
    fileSizeLimit: { default: FILE_UPLOAD_CONSTANTS.SCAN_UPLOAD.DEFAULT_SIZE_LIMIT },
    extension: { default: '' },
    label: { default: '' },
    readonly: { type: Boolean, default: false },
    isOptional: { type: Boolean, default: false },
    serverUrl: { default: FILE_UPLOAD_CONSTANTS.SCAN_UPLOAD.DEFAULT_SERVER_URL },
    modelValue: { default: () => [] },
    error: { default: null },
    hideUploadedFiles: { type: Boolean, default: false },
    hideProgress: { type: Boolean, default: false },
    patientId: { default: '' },
    appendScanParams: { type: Boolean, default: true },
    dropzoneHeight: { default: '10rem' },
    fetchFileInfo: { type: Function, default: null }
  },
  emits: ["update:modelValue", "change", "uploading"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

const { t } = useI18n();
const sessionStore = useSessionStore();

// ===== TYPE DEFINITIONS =====
/**
 * Props interface for ScanUploader component
 */
const props = __props;

const emit = __emit;

// ===== REACTIVE STATE =====

const filePond = ref<any>(null);
const fileList = ref<Array<FileAnswer | FilePondFile>>([]);
const files = ref<Array<FilePondFile | FilePondInitialFile | FileUploaded>>([]);
const uploadingFiles = ref<Array<FileAnswer>>([]);
const fileToRemove = ref<FileAnswer | null>(null);
const notificationStore = useNotificationStore();

// ===== COMPUTED PROPERTIES =====

// FilePond's runtime enum is not always available in test/build environments.
// Provide a safe mapping that falls back to known numeric values.
const FileStatusValue = {
  PROCESSING: (FileStatus as any)?.PROCESSING ?? 3,
  PROCESSING_QUEUED: (FileStatus as any)?.PROCESSING_QUEUED ?? 9,
  PROCESSING_COMPLETE: (FileStatus as any)?.PROCESSING_COMPLETE ?? 5
};

const serverUrlWithParams = computed((): string => {
  if (!props.appendScanParams) {
    return props.serverUrl;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const qp = new URLSearchParams();
  if (props.patientId) {
    qp.set('patientId', props.patientId);
  }
  if (timezone) {
    qp.set('clientTimezone', timezone);
  }

  const hasQuery = props.serverUrl.includes('?');
  const joiner = hasQuery ? '&' : '?';
  return `${props.serverUrl}${qp.toString() ? joiner + qp.toString() : ''}`;
});

const sizeLimitLabel = computed((): string => t('platform.file-upload.max-size-note', [props.fileSizeLimit]));

const labelIdle = computed((): string => `
<div class="flex flex-col items-center justify-center text-center space-y-4 px-8 py-10">
  <div class="flex flex-col items-center space-y-3">
    <div class="flex items-center space-x-3 text-gray-600">
      <span class="text-base font-medium">${t('platform.file-upload.drag-drop')}</span>
      <span class="filepond--label-action btn btn-regular btn-ghost">${t('platform.file-upload.browse')}</span>
    </div>
    <div class="text-sm text-gray-500">${sizeLimitLabel.value}</div>
    <div class="text-sm text-gray-500">${t('custom.menicon.scans.accepted-format')}: .mxf</div>
  </div>
</div>`);
const allowFileTypeValidation = computed((): boolean =>
  !extension.value.includes('.mxf')
);
const extension = computed((): string => props.extension || FILE_UPLOAD_CONSTANTS.SCAN_UPLOAD.DEFAULT_EXTENSION);

const acceptedTypes = computed((): string => {
  const ext = extension.value;
  // For .mxf files, allow any file type since they don't have a standard MIME type
  if (ext.includes('.mxf')) {
    return '*';
  }
  return ext;
});

const answer = computed((): FileAnswer[] => fileList.value.map((file: FileAnswer | FilePondFile) => {
  if ('serverId' in file) {
    try {
      const parsed = JSON.parse((file as FilePondFile).serverId as unknown as string);
      const payload: any = parsed?.data ?? parsed;
      const id = payload?.id ?? (file as FilePondFile).id;
      const filename = payload?.filename ?? (file as FilePondFile).filename;
      const mime = payload?.mime_type ?? (file as FilePondFile).fileType;
      const createdAt = payload?.created_at ?? new Date().toISOString();
      return {
        id,
        originalId: (file as FilePondFile).id,
        name: filename,
        size: (file as FilePondFile).fileSize,
        type: mime,
        extension: (file as FilePondFile).fileExtension,
        status: (file as FilePondFile).status,
        created_at: createdAt
      };
    } catch (_) {
      return {
        id: (file as FilePondFile).id,
        originalId: (file as FilePondFile).id,
        name: (file as FilePondFile).filename,
        size: (file as FilePondFile).fileSize,
        type: (file as FilePondFile).fileType,
        extension: (file as FilePondFile).fileExtension,
        status: (file as FilePondFile).status,
        created_at: new Date().toISOString()
      };
    }
  }
  return file as FileAnswer;
}));

const server = computed((): FilePondServerConfigProps['server'] => ({
  url: `${apiClient.defaults.baseURL}${serverUrlWithParams.value}`,
  headers: {
    ...(cookieService.getXsrfToken() ? { [apiClient.defaults.xsrfHeaderName]: cookieService.getXsrfToken() } : {}),
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'User-Agent': 'MeniconAppUploader',
    ...(tokenService.getToken() ? { Authorization: `Bearer ${tokenService.getToken()}` } : {}),
    ...(sessionStore.currentOrganisationId ? { 'Current-Organisation': sessionStore.currentOrganisationId } : {})
  },
  process: {
    url: '',
    ondata: (formData: FormData) => {
      formData.append('options[allow_all_types]', '1');
      return formData;
    },
    onload: (response: string) => response,
    onerror: (response: string) => {
      try {
        const parsed = JSON.parse(response);
        if (parsed && typeof parsed === 'object') {
          if (parsed.message) {
            return parsed.message as string;
          }
          if (parsed.error) {
            return parsed.error as string;
          }
          if (typeof parsed.details === 'string') {
            try {
              const details = JSON.parse(parsed.details);
              if (details?.message) {
                return details.message as string;
              }
            } catch (_) {
              // ignore nested parse errors
            }
          }
        }
      } catch (_) {
        // not JSON, fall through
      }
      return response || t('platform.file-upload.general-error-message');
    }
  },
  revert: {
    url: '/',
    headers: {
      ...(cookieService.getXsrfToken() ? { [apiClient.defaults.xsrfHeaderName]: cookieService.getXsrfToken() } : {}),
      'Content-type': 'application/json'
    }
  }
}));

const isFileUploading = computed((): boolean => !!uploadingFiles.value
  .find((file) => file.status === FileStatusValue.PROCESSING || file.status === FileStatusValue.PROCESSING_QUEUED)
);

const errorClass = computed((): string => props.error ? 'border-danger-700' : '');

// ===== WATCHERS =====
watch(isFileUploading, (current, previous) => {
  if (previous !== current) {
    emit('uploading', isFileUploading.value);
  }
});

// ===== EVENT HANDLERS =====
/**
 * Handle file addition to the upload queue
 * @param error - Error object if file addition failed
 * @param file - The FilePond file object
 */
const handleAddFile = (error: { main: string; sub: string } | null, file: FilePondFile): void => {
  if (error) {
    uploadingFiles.value.push({
      id: file.id,
      originalId: file.id,
      name: file.filename,
      size: file.fileSize,
      type: file.fileType,
      extension: file.fileExtension,
      status: file.status,
      progress: 0,
      error: error.main
    } as any);
  } else {
    uploadingFiles.value.push({
      id: file.id,
      originalId: file.id,
      name: file.filename,
      size: file.fileSize,
      type: file.fileType,
      extension: file.fileExtension,
      status: file.status,
      progress: 0
    } as any);
  }
};

const handleProcessFileStart = (file: FilePondFile): void => {
  const fileIndex = uploadingFiles.value.findIndex((f) => f.originalId === file.id);
  if (fileIndex !== -1) {
    uploadingFiles.value[fileIndex].status = file.status;
  }
};

const handleProcessFileProgress = (processingFile: FilePondFile, progress: number): void => {
  const fileIndex = uploadingFiles.value.findIndex((file) => file.originalId === processingFile.id);
  if (fileIndex !== -1) {
    uploadingFiles.value[fileIndex].progress = progress;
  }
};

/**
 * Handle file processing completion
 * @param error - Error object if processing failed
 * @param processFile - The processed FilePond file object
 */
const handleProcessFile = (error: FilePondErrorDescription | null, processFile: FilePondFile): void => {
  if (error) {
    let friendlyError: string = t('platform.file-upload.general-error-message');
    if (typeof error === 'string') {
      friendlyError = error;
    } else if (error && typeof (error as any).main === 'string') {
      friendlyError = (error as any).main;
    } else if (error && typeof (error as any).message === 'string') {
      friendlyError = (error as any).message;
    } else if (error && typeof (error as any).body === 'string') {
      friendlyError = (error as any).body;
    }
    uploadingFiles.value = uploadingFiles.value.map((file) =>
      file.originalId === processFile.id
        ? {
          ...file,
          status: processFile.status,
          error: friendlyError
        }
        : file
    );
  } else {
    fileList.value.push(processFile);

    let returnedId: string | undefined;
    let returnedName: string | undefined;
    try {
      if ((processFile as any).serverId) {
        const parsed = JSON.parse((processFile as any).serverId);
        const payload: any = parsed?.data ?? parsed;
        if (payload && typeof payload === 'object') {
          returnedId = payload.id;
          returnedName = payload.filename;
        } else if (typeof payload === 'string') {
          returnedId = payload as string;
        }
      }
    } catch (_) {
      // ignore parse errors; fallback to FilePond fields
    }

    const safeId = returnedId ?? processFile.id;
    const safeName = returnedName ?? processFile.filename;

    uploadingFiles.value = uploadingFiles.value.map((file) =>
      file.originalId === processFile.id
        ? {
          ...file,
          status: processFile.status,
          id: safeId,
          name: safeName
        }
        : file
    );
  }
};

const handleRemoveFile = (error: FilePondErrorDescription | null, file: FilePondFile): void => {
  if (!error) {
    fileList.value = fileList.value.filter((data: FileAnswer | FilePondFile) => {
      const fileName = 'serverId' in data ? (data as any).filename : (data as any).name;
      return fileName !== file.filename;
    });

    uploadingFiles.value = uploadingFiles.value.filter((data: FileAnswer) => {
      const fileName = data.name;
      return fileName !== file.filename;
    });

    exposed.save();
  }
};

const handleAbort = (targetFile: FilePondFile): void => {
  uploadingFiles.value = uploadingFiles.value.filter((file: FileAnswer) => targetFile.id !== file.id);
};

// ===== UTILITY FUNCTIONS =====
const updateFiles = (): void => {
  files.value = fileList.value.map((file: FileAnswer | FilePondFile) => {
    if ('serverId' in file) {
      return file as any;
    }
    return {
      source: (file as FileAnswer).id,
      options: {
        type: 'local',
        file: {
          name: (file as FileAnswer).name,
          size: (file as FileAnswer).size,
          type: (file as FileAnswer).type
        }
      }
    };
  });
};

/**
 * Fetch file information from the server
 * @param fileId - The file ID to fetch information for
 * @param filename - Optional filename fallback
 * @returns Promise resolving to FileAnswer or null
 */
const fetchFileInfoFromProp = async (fileId: string, filename?: string): Promise<FileAnswer | null> => {
  if (props.fetchFileInfo) {
    try {
      return await props.fetchFileInfo(fileId, filename);
    } catch (_) {
      return null;
    }
  }
  return null;
};

// ===== WATCHERS =====
watch(() => props.modelValue, async (newValue) => {
  if (newValue) {
    fileList.value = [...newValue];
    uploadingFiles.value = [...newValue];
    updateFiles();

    const filesWithInfo = await Promise.all(
      newValue.map(async (file) => {
        if (!file.size || file.size === 0) {
          const fileInfo = await fetchFileInfoFromProp(file.id, file.name);
          return fileInfo || file;
        }
        return file;
      })
    );

    uploadingFiles.value = filesWithInfo as FileAnswer[];
  } else {
    fileList.value = [];
    uploadingFiles.value = [];
    updateFiles();
  }
}, { deep: true, immediate: true });

const save = (): void => {
  updateFiles();
  emit('update:modelValue', answer.value);
  emit('change', answer.value);
};

const getProgress = (progress: string): string => `${(parseFloat(progress) * 100).toFixed(2)}`;

const findFileIndexFromInstance = (targetFile: FileAnswer): number | undefined => {
  if (filePond.value) {
    const allFiles = (filePond.value as any).getFiles();
    if ('isNew' in targetFile) {
      return allFiles.findIndex(
        (file: FilePondFile) => (targetFile as any).isNew === true && targetFile.originalId === file.id
      );
    }
    return allFiles.findIndex((file: FilePondFile) => targetFile.id === file.serverId);
  }
  return;
};

const removeFile = (targetFile: FileAnswer): void => {
  try {
    if (filePond.value) {
      const allFiles = (filePond.value as any).getFiles();
      const fileIndex = findFileIndexFromInstance(targetFile);
      if (typeof fileIndex === 'number') {
        const uploadingFileIndex = uploadingFiles.value.findIndex((file: FileAnswer) => targetFile.id === file.id);
        (filePond.value as any).removeFile(allFiles[fileIndex], { revert: true });
        uploadingFiles.value.splice(uploadingFileIndex, 1);
      }
    }
  } catch (e) {
    handleError();
  } finally {
    fileToRemove.value = null;
  }
};

const dismissFile = (targetFile: FileAnswer): void => {
  uploadingFiles.value = uploadingFiles.value.filter((file: FileAnswer) => file.id !== targetFile.id);
};

const cancelFile = (targetFile: FileAnswer): void => {
  try {
    if (filePond.value) {
      const allFiles = (filePond.value as any).getFiles();
      const fileIndex = findFileIndexFromInstance(targetFile);
      if (allFiles && allFiles[fileIndex] && typeof fileIndex === 'number') {
        allFiles[fileIndex].abortProcessing();
      }
    }
  } catch (e) {
    handleError();
  }
};

const handleError = (): void => {
  notificationStore.setErrorToast(t('platform.error.request-failure'));
};


// ===== EXPOSED METHODS =====

const exposed = {
  save,
  extension,
  allowFileTypeValidation,
  acceptedTypes
};

__expose(exposed);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (!_ctx.readonly || uploadingFiles.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(InputTextLabel, {
            label: _ctx.label,
            "is-optional": _ctx.isOptional
          }, null, 8, ["label", "is-optional"]),
          _createElementVNode("div", {
            class: "pr-1",
            style: _normalizeStyle({ '--uploader-height': _ctx.dropzoneHeight })
          }, [
            (uploadingFiles.value.length > 0 && !_ctx.hideUploadedFiles)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uploadingFiles.value, (file, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: index,
                        class: _normalizeClass([uploadingFiles.value.length !== index + 1 ? 'border-b' : ''])
                      }, [
                        _createElementVNode("div", _hoisted_4, [
                          _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("div", _hoisted_6, [
                              (file.status === FileStatusValue.PROCESSING_COMPLETE || !file.hasOwnProperty('status'))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                    (!_ctx.readonly)
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                          _createVNode(BaseIcon, {
                                            name: "check",
                                            class: "mr-2"
                                          }),
                                          _createTextVNode(_toDisplayString(_ctx.$t('platform.file-upload.uploaded')), 1)
                                        ]))
                                      : _createCommentVNode("", true),
                                    _createElementVNode("div", _hoisted_9, [
                                      _createElementVNode("div", _hoisted_10, _toDisplayString(file.name) + " - ", 1),
                                      _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(formatBytes)(file.size)), 1)
                                    ])
                                  ]))
                                : (file.error && !_ctx.readonly)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                      _createElementVNode("div", _hoisted_13, [
                                        _createVNode(BaseIcon, {
                                          name: "warning",
                                          class: "mr-2",
                                          height: "1rem"
                                        }),
                                        _createTextVNode(_toDisplayString(file.error), 1)
                                      ]),
                                      _createElementVNode("div", _hoisted_14, [
                                        _createElementVNode("div", _hoisted_15, _toDisplayString(file.name) + " - ", 1),
                                        _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(formatBytes)(file.size)), 1)
                                      ])
                                    ]))
                                  : (!_ctx.readonly)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                        (!_ctx.hideProgress)
                                          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                              _createVNode(BaseIcon, {
                                                name: "loader",
                                                class: "mr-2"
                                              }),
                                              _createTextVNode(_toDisplayString(_ctx.$t('platform.file-upload.file-processing')) + " - " + _toDisplayString(getProgress(((file as any).progress || 0).toString())) + "% ", 1)
                                            ]))
                                          : _createCommentVNode("", true),
                                        _createElementVNode("div", _hoisted_19, [
                                          _createElementVNode("div", _hoisted_20, _toDisplayString(file.name) + " - ", 1),
                                          _createElementVNode("div", _hoisted_21, _toDisplayString(_unref(formatBytes)(file.size)), 1)
                                        ])
                                      ]))
                                    : _createCommentVNode("", true)
                            ])
                          ]),
                          (!_ctx.readonly)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                (file.status === FileStatusValue.PROCESSING || file.status === FileStatusValue.PROCESSING_QUEUED)
                                  ? (_openBlock(), _createBlock(MedKitButton, {
                                      key: 0,
                                      "data-dusk": "abort-processing-button",
                                      label: _ctx.$t('platform.file-upload.button-abort-item-processing'),
                                      kind: "secondary",
                                      size: "small",
                                      onClick: ($event: any) => (cancelFile(file))
                                    }, null, 8, ["label", "onClick"]))
                                  : _createCommentVNode("", true),
                                ((file.status === FileStatusValue.PROCESSING_COMPLETE || !Object.prototype.hasOwnProperty.call(file, 'status')) && !isFileUploading.value)
                                  ? (_openBlock(), _createBlock(MedKitButton, {
                                      key: 1,
                                      "data-dusk": "remove-uploaded-file-button",
                                      label: _ctx.$t('platform.file-upload.button-remove-item'),
                                      kind: "tertiary",
                                      size: "small",
                                      onClick: ($event: any) => (fileToRemove.value = file)
                                    }, null, 8, ["label", "onClick"]))
                                  : _createCommentVNode("", true),
                                (file.error)
                                  ? (_openBlock(), _createBlock(MedKitButton, {
                                      key: 2,
                                      "data-dusk": "dismiss-upload-error-button",
                                      label: _ctx.$t('platform.common.dismiss'),
                                      kind: "secondary",
                                      size: "small",
                                      onClick: ($event: any) => (dismissFile(file))
                                    }, null, 8, ["label", "onClick"]))
                                  : _createCommentVNode("", true)
                              ], 64))
                            : _createCommentVNode("", true)
                        ]),
                        (file.status === FileStatusValue.PROCESSING && !_ctx.hideProgress)
                          ? (_openBlock(), _createBlock(LoadingBar, {
                              key: 0,
                              class: "mb-4 -mt-2",
                              value: ((file as any).progress || 0) * 100,
                              "loading-bar-height": "8px",
                              color: "success-500",
                              "out-custom-class": "h-5 relative w-full rounded-full overflow-hidden bg-primary-50",
                              "inner-custom-class": "m-1.5 rounded"
                            }, null, 8, ["value"]))
                          : _createCommentVNode("", true)
                      ], 2))
                    }), 128))
                  ])
                ]))
              : _createCommentVNode("", true),
            _createVNode(_unref(FilePond), {
              ref_key: "filePond",
              ref: filePond,
              name: "scanFile",
              "data-dusk": "filepond",
              "max-parallel-uploads": _unref(FILE_UPLOAD_CONSTANTS).SCAN_UPLOAD.MAX_PARALLEL_UPLOADS,
              "allow-multiple": true,
              "instant-upload": true,
              class: _normalizeClass(['mt-4 h-full block', errorClass.value]),
              "label-idle": labelIdle.value,
              "label-invalid-field": _ctx.$t('platform.file-upload.invalid-field'),
              "label-file-waiting-for-size": _ctx.$t('platform.file-upload.file-waiting-for-size'),
              "label-file-size-not-available": _ctx.$t('platform.file-upload.file-size-not-available'),
              "label-file-loading": _ctx.$t('platform.file-upload.file-loading'),
              "label-file-load-error": _ctx.$t('platform.file-upload.file-load-error'),
              "label-file-processing": _ctx.$t('platform.file-upload.file-processing'),
              "label-file-processing-complete": _ctx.$t('platform.file-upload.file-processing-complete'),
              "label-file-processing-aborted": _ctx.$t('platform.file-upload.file-processing-aborted'),
              "label-file-processing-error": _ctx.$t('platform.file-upload.file-processing-error'),
              "label-file-processing-revert-error": _ctx.$t('platform.file-upload.file-processing-revert-error'),
              "label-file-remove-error": _ctx.$t('platform.file-upload.file-remove-error'),
              "label-tap-to-cancel": _ctx.$t('platform.file-upload.tap-to-cancel'),
              "label-tap-to-retry": _ctx.$t('platform.file-upload.tap-to-retry'),
              "label-tap-to-undo": _ctx.$t('platform.file-upload.tap-to-undo'),
              "label-button-remove-item": _ctx.$t('platform.file-upload.button-remove-item'),
              "label-button-abort-item-load": _ctx.$t('platform.file-upload.button-abort-item-load'),
              "label-button-retry-item-load": _ctx.$t('platform.file-upload.button-retry-item-load'),
              "label-button-abort-item-processing": _ctx.$t('platform.file-upload.button-abort-item-processing'),
              "label-button-undo-item-processing": _ctx.$t('platform.file-upload.button-undo-item-processing'),
              "label-button-retry-item-processing": _ctx.$t('platform.file-upload.button-retry-item-processing'),
              "label-button-process-item": _ctx.$t('platform.file-upload.button-process-item'),
              "label-max-file-size-exceeded": _ctx.$t('platform.file-upload.max-file-size-exceeded'),
              "label-max-file-size": _ctx.$t('platform.file-upload.max-file-size'),
              "label-max-total-file-size": _ctx.$t('platform.file-upload.max-total-file-size'),
              "label-file-type-not-allowed": _ctx.$t('platform.file-upload.file-type-not-allowed'),
              "file-validate-type-label-expected-types": _ctx.$t('platform.file-upload.file-validate-type-label-expected-types'),
              "image-validate-size-label-format-error": _ctx.$t('platform.file-upload.image-validate-size-label-format-error'),
              "image-validate-size-label-image-size-too-small": _ctx.$t('platform.file-upload.image-validate-size-label-image-size-too-small'),
              "image-validate-size-label-image-size-too-big": _ctx.$t('platform.file-upload.image-validate-size-label-image-size-too-big'),
              "image-validate-size-label-expected-min-size": _ctx.$t('platform.file-upload.image-validate-size-label-expected-min-size'),
              "image-validate-size-label-expected-max-size": _ctx.$t('platform.file-upload.image-validate-size-label-expected-max-size'),
              "image-validate-size-label-image-resolution-too-low": _ctx.$t('platform.file-upload.image-validate-size-label-image-resolution-too-low'),
              "image-validate-size-label-image-resolution-too-high": _ctx.$t('platform.file-upload.image-validate-size-label-image-resolution-too-high'),
              "image-validate-size-label-expected-min-resolution": _ctx.$t('platform.file-upload.image-validate-size-label-expected-min-resolution'),
              "image-validate-size-label-expected-max-resolution": _ctx.$t('platform.file-upload.image-validate-size-label-expected-max-resolution'),
              "allow-file-type-validation": allowFileTypeValidation.value,
              "accepted-file-types": acceptedTypes.value,
              server: server.value,
              files: files.value,
              "max-file-size": _ctx.fileSizeLimit,
              onAddfile: handleAddFile,
              onProcessfile: handleProcessFile,
              onProcessfilestart: handleProcessFileStart,
              onProcessfileprogress: handleProcessFileProgress,
              onRemovefile: handleRemoveFile,
              onProcessfiles: save,
              onProcessfileabort: handleAbort
            }, null, 8, ["max-parallel-uploads", "class", "label-idle", "label-invalid-field", "label-file-waiting-for-size", "label-file-size-not-available", "label-file-loading", "label-file-load-error", "label-file-processing", "label-file-processing-complete", "label-file-processing-aborted", "label-file-processing-error", "label-file-processing-revert-error", "label-file-remove-error", "label-tap-to-cancel", "label-tap-to-retry", "label-tap-to-undo", "label-button-remove-item", "label-button-abort-item-load", "label-button-retry-item-load", "label-button-abort-item-processing", "label-button-undo-item-processing", "label-button-retry-item-processing", "label-button-process-item", "label-max-file-size-exceeded", "label-max-file-size", "label-max-total-file-size", "label-file-type-not-allowed", "file-validate-type-label-expected-types", "image-validate-size-label-format-error", "image-validate-size-label-image-size-too-small", "image-validate-size-label-image-size-too-big", "image-validate-size-label-expected-min-size", "image-validate-size-label-expected-max-size", "image-validate-size-label-image-resolution-too-low", "image-validate-size-label-image-resolution-too-high", "image-validate-size-label-expected-min-resolution", "image-validate-size-label-expected-max-resolution", "allow-file-type-validation", "accepted-file-types", "server", "files", "max-file-size"])
          ], 4)
        ]))
      : _createCommentVNode("", true),
    (fileToRemove.value)
      ? (_openBlock(), _createBlock(ActionModal, {
          key: 1,
          icon: "trash",
          "icon-color": "danger",
          "show-close": false,
          title: _ctx.$t('platform.file-upload.remove-confirmation.title'),
          body: _ctx.$t('platform.file-upload.remove-confirmation.body'),
          "primary-action": _ctx.$t('platform.file-upload.remove-confirmation.action'),
          "tertiary-action": _ctx.$t('platform.common.cancel'),
          onClose: _cache[0] || (_cache[0] = ($event: any) => (fileToRemove.value = null)),
          onTertiaryClick: _cache[1] || (_cache[1] = ($event: any) => (fileToRemove.value = null)),
          onPrimaryClick: _cache[2] || (_cache[2] = ($event: any) => (removeFile(fileToRemove.value as FileAnswer)))
        }, null, 8, ["title", "body", "primary-action", "tertiary-action"]))
      : _createCommentVNode("", true)
  ]))
}
}

})